import { useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ArrowLeft, GitBranch, ExternalLink, X } from "lucide-react";
import { api } from "@/api/client";
import { DiffPreview } from "@/components/DiffPreview";
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Tabs, TabsContent, TabsList, TabsTrigger } from "@fox1ck-org/ui";

export default function GitOpsForm() {
  const { name } = useParams<{ name: string }>();
  const [searchParams] = useSearchParams();
  const action = (searchParams.get("action") ?? "scale") as "scale" | "resources" | "env";

  const [replicas, setReplicas] = useState(2);
  const [requestsCpu, setRequestsCpu] = useState("100m");
  const [requestsMemory, setRequestsMemory] = useState("256Mi");
  const [limitsCpu, setLimitsCpu] = useState("500m");
  const [limitsMemory, setLimitsMemory] = useState("512Mi");
  const [envEntries, setEnvEntries] = useState<Array<{ key: string; value: string }>>([]);
  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [removeKeys] = useState<string[]>([]);
  const [prResult, setPrResult] = useState<{ number: number; url: string } | null>(null);

  const { data: manifest } = useQuery({
    queryKey: ["gitops-manifest", name],
    queryFn: () => api.gitops.getManifest(name!),
    enabled: !!name,
  });

  const scaleMutation = useMutation({
    mutationFn: () => api.gitops.scale(name!, replicas),
    onSuccess: (data) => setPrResult(data.pr),
  });

  const resourcesMutation = useMutation({
    mutationFn: () =>
      api.gitops.resources(name!, {
        requests: { cpu: requestsCpu, memory: requestsMemory },
        limits: { cpu: limitsCpu, memory: limitsMemory },
      }),
    onSuccess: (data) => setPrResult(data.pr),
  });

  const envMutation = useMutation({
    mutationFn: () =>
      api.gitops.env(name!, {
        set: envEntries.reduce((acc, { key, value }) => ({ ...acc, [key]: value }), {} as Record<string, string>),
        remove: removeKeys.length > 0 ? removeKeys : undefined,
      }),
    onSuccess: (data) => setPrResult(data.pr),
  });

  const isSubmitting = scaleMutation.isPending || resourcesMutation.isPending || envMutation.isPending;
  const submitError = scaleMutation.error || resourcesMutation.error || envMutation.error;

  const handleSubmit = () => {
    if (action === "scale") scaleMutation.mutate();
    else if (action === "resources") resourcesMutation.mutate();
    else if (action === "env") envMutation.mutate();
  };

  const addEnvEntry = () => {
    if (newKey && newValue) {
      setEnvEntries([...envEntries, { key: newKey, value: newValue }]);
      setNewKey("");
      setNewValue("");
    }
  };

  if (prResult) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Card className="border-green-500/30 bg-green-500/5">
          <CardContent className="p-6 text-center">
            <GitBranch className="h-12 w-12 text-green-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-foreground mb-2">Pull request создан</h2>
            <p className="text-muted-foreground mb-4">PR #{prResult.number}</p>
            <Button asChild>
              <a href={prResult.url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" /> Открыть в GitHub
              </a>
            </Button>
          </CardContent>
        </Card>
        <div className="flex justify-center">
          <Button variant="ghost" asChild>
            <Link to={`/apps/${name}`}>К приложению</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/apps/${name}`}><ArrowLeft className="h-5 w-5" /></Link>
        </Button>
        <div>
          <h1 className="text-[17px] font-semibold text-foreground">
            GitOps: {action === "scale" ? "реплики" : action === "resources" ? "ресурсы" : "окружение"}
          </h1>
          <p className="text-sm text-muted-foreground">{name}</p>
        </div>
      </div>

      {submitError && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive text-sm">
          {submitError.message}
        </div>
      )}

      <Tabs value={action}>
        <TabsList className="w-full">
          <TabsTrigger value="scale" asChild className="flex-1">
            <Link to={`/apps/${name}/gitops?action=scale`}>Масштаб</Link>
          </TabsTrigger>
          <TabsTrigger value="resources" asChild className="flex-1">
            <Link to={`/apps/${name}/gitops?action=resources`}>Ресурсы</Link>
          </TabsTrigger>
          <TabsTrigger value="env" asChild className="flex-1">
            <Link to={`/apps/${name}/gitops?action=env`}>Переменные</Link>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="scale">
          <Card>
            <CardHeader><CardTitle>Реплики</CardTitle></CardHeader>
            <CardContent>
              <label className="block text-sm font-medium text-muted-foreground mb-2">Реплики</label>
              <input
                type="range"
                min={0}
                max={10}
                value={replicas}
                onChange={(e) => setReplicas(parseInt(e.target.value, 10))}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-muted-foreground mt-1">
                <span>0</span>
                <span className="text-foreground font-bold text-lg">{replicas}</span>
                <span>10</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="resources">
          <Card>
            <CardHeader><CardTitle>Лимиты ресурсов</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">Запрос CPU</label>
                  <Input value={requestsCpu} onChange={(e) => setRequestsCpu(e.target.value)} placeholder="100m" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">Лимит CPU</label>
                  <Input value={limitsCpu} onChange={(e) => setLimitsCpu(e.target.value)} placeholder="500m" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">Запрос памяти</label>
                  <Input value={requestsMemory} onChange={(e) => setRequestsMemory(e.target.value)} placeholder="256Mi" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">Лимит памяти</label>
                  <Input value={limitsMemory} onChange={(e) => setLimitsMemory(e.target.value)} placeholder="512Mi" />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="env">
          <Card>
            <CardHeader><CardTitle>Переменные окружения</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {envEntries.map((entry, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={entry.key} readOnly className="flex-1 opacity-70" />
                  <Input
                    value={entry.value}
                    onChange={(e) => {
                      const updated = [...envEntries];
                      updated[i] = { ...entry, value: e.target.value };
                      setEnvEntries(updated);
                    }}
                    className="flex-1"
                  />
                  <Button variant="ghost" size="icon" onClick={() => setEnvEntries(envEntries.filter((_, j) => j !== i))}>
                    <X className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
              <div className="flex items-center gap-2 pt-2">
                <Input placeholder="KEY" value={newKey} onChange={(e) => setNewKey(e.target.value)} className="flex-1" />
                <Input placeholder="value" value={newValue} onChange={(e) => setNewValue(e.target.value)} className="flex-1" />
                <Button variant="secondary" onClick={addEnvEntry} disabled={!newKey || !newValue}>Добавить</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {manifest && <DiffPreview title="Текущий манифест" content={manifest.content} path={manifest.path} />}

      <div className="flex justify-end gap-4">
        <Button variant="ghost" asChild>
          <Link to={`/apps/${name}`}>Отмена</Link>
        </Button>
        <Button onClick={handleSubmit} disabled={isSubmitting}>
          <GitBranch className="h-4 w-4" />
          {isSubmitting ? "Создаём PR…" : "Создать pull request"}
        </Button>
      </div>
    </div>
  );
}
